import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { startProcurementTrial } from '../api/procurement';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useProcurementAccess } from '../context/ProcurementAccessContext';
import { procurementTrialCopy } from '../locales/procurementTrial';
import { analytics } from '../analytics';
import { authLink } from '../utils/authReturnPath';

export default function ProcurementTrialPanel({ compact = false, showActive = true }: { compact?: boolean; showActive?: boolean }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const copy = procurementTrialCopy[language];
  const { access, loading, error, refresh, allowed } = useProcurementAccess();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const pending = useRef<object | null>(null);
  const owner = useRef(user?.email); owner.current = user?.email;
  useEffect(() => {
    pending.current = null; setBusy(false); setFailure('');
    return () => { pending.current = null; };
  }, [user?.email]);
  const trial = access?.trial;
  const trialFeatures = ['lots', 'suppliers', 'search_alerts'] as const;
  const hasFullAccess = trialFeatures.every(feature => allowed(feature));
  const hasSomeAccess = trialFeatures.some(feature => allowed(feature));
  const serviceLimited = access?.settings?.lots_enabled === false || access?.settings?.suppliers_enabled === false || access?.settings?.search_alerts_enabled === false;
  if (user?.role && user.role !== 'user') return null;
  if (trial?.status === 'active' && !showActive) return null;
  if ((trial?.status === 'unavailable' || trial?.status === 'expired') && hasFullAccess) return null;
  const start = async () => {
    if (pending.current) return;
    const attempt = {}; pending.current = attempt; setBusy(true); setFailure('');
    const startedBy = owner.current;
    try { await startProcurementTrial(); if (pending.current === attempt && owner.current === startedBy) { if (trial?.status === 'available') analytics.trialStarted(); await refresh(); } }
    catch (failure: any) {
      if (pending.current !== attempt || owner.current !== startedBy) return;
      const code = failure?.response?.data?.detail?.code;
      setFailure(code === 'procurement_trial_used' ? copy.used : code === 'procurement_trial_ineligible' ? copy.ineligible
        : code === 'procurement_trial_unavailable' ? copy.unavailableError : copy.startError);
      if (failure?.response?.status === 409) await refresh();
    } finally { if (pending.current === attempt) { pending.current = null; setBusy(false); } }
  };
  const expired = trial?.status === 'expired';
  const active = trial?.status === 'active';
  const available = trial?.status === 'available';
  const title = !user ? copy.guestTitle : active ? copy.activeTitle : expired ? copy.expiredTitle : available ? copy.availableTitle : copy.unavailableTitle;
  const hint = !user ? copy.guestHint : active ? serviceLimited ? copy.activeLimitedHint : copy.activeHint
    : expired ? hasSomeAccess ? copy.expiredWithAccessHint : copy.expiredHint : available ? copy.availableHint : copy.unavailable;
  const expiry = trial?.expires_at ? new Date(trial.expires_at) : null;
  const concise = compact || active;
  const actions = <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
        {active && allowed('lots') && location.pathname !== '/lots' && <Link className="procurement-button procurement-button-primary" to="/lots">{copy.openSearch}</Link>}
        {!user ? <Link className="procurement-button procurement-button-primary" to={authLink('/login', location.pathname + location.search + location.hash)}>{copy.login}</Link>
          : error ? <button className="procurement-button" onClick={() => void refresh()}>{copy.retry}</button>
          : available ? <button disabled={busy} className="procurement-button procurement-button-primary" onClick={() => void start()}>{busy && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}{copy.start}</button>
          : <Link className={active ? 'inline-flex min-h-[44px] items-center text-sm font-medium text-slate-600 hover:text-goszakup-700' : 'procurement-button'} to="/plans">{copy.plans}</Link>}
        {active && allowed('search_alerts') && !access?.telegram_linked && <Link className="inline-flex min-h-[44px] items-center text-sm font-semibold text-goszakup-700 underline-offset-4 hover:underline" to="/profile#notifications">{copy.profile}</Link>}
      </div>;
  return <section className={`rounded-xl border ${active ? 'border-slate-200 bg-white' : 'border-goszakup-200 bg-goszakup-50/40'} ${concise ? 'px-4 py-3 sm:px-5' : 'p-5 sm:p-6'}`} aria-busy={busy}>
    {loading ? <p role="status" className="flex items-center gap-2 text-sm"><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />{copy.loading}</p> : <div className={concise ? 'flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between xl:gap-6' : 'space-y-4'}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1"><h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {active && expiry && Number.isFinite(expiry.getTime()) && <p className="text-xs text-slate-500">{copy.until}: <time dateTime={trial?.expires_at || undefined}>{expiry.toLocaleDateString(language === 'kk' ? 'kk-KZ' : 'ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</time></p>}
        </div>
        {(!active || error || serviceLimited) && <p className="mt-1 text-sm leading-6 text-slate-600">{error ? copy.accessError : hint}</p>}
        {available && <p className="mt-1 text-xs leading-5 text-slate-500">{copy.terms}</p>}
        {failure && <p role="alert" className="mt-2 text-sm text-red-700">{failure}</p>}
      </div>
      {actions}
    </div>}
  </section>;
}
